import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, Bot, X, ExternalLink, ChevronRight } from 'lucide-react';
import BBALoader from '../components/BBALoader';
import { useTheme } from '../contexts/ThemeContext';
import {
  AGENT_DEPARTMENTS,
  AGENT_SECTIONS,
  listAgentTemplates,
  getAgentGroupEmojis,
} from '../services/agentTemplatesService';
import { filterResourcesBySearch } from '../services/technologyCommunityService';
import PortalBackground from '../components/PortalBackground';

const isUrl = (s) => /^https?:\/\//i.test(String(s || '').trim());

const GifTile = ({ agent, className }) => (
  <span className={`shrink-0 overflow-hidden bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 flex items-center justify-center ${className}`}>
    {agent.gif?.url ? (
      <img src={agent.gif.url} alt="" className="w-full h-full object-cover" />
    ) : (
      <Bot className="w-1/2 h-1/2" />
    )}
  </span>
);

/** Agent card matching the Skills / JobTread Imports card style, with a full-width View action. */
const AgentCard = ({ agent, onOpen }) => {
  const { darkMode } = useTheme();
  const version = String(agent.version || '').trim() || '1.0';
  const counts = AGENT_SECTIONS.map((s) => ({ ...s, n: agent[s.key]?.length || 0 })).filter((s) => s.n);

  return (
    <div
      className={`rounded-xl border p-4 transition-shadow hover:shadow-md flex flex-col h-full ${
        darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-center gap-3 mb-3">
        <GifTile agent={agent} className="w-14 h-14 rounded-lg" />
        <div className="min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            <h4 className={`text-base md:text-lg font-bold leading-snug truncate ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {agent.name}
            </h4>
            <span
              className={`shrink-0 px-1.5 py-0.5 rounded-full text-[10px] font-semibold leading-none ${
                darkMode ? 'bg-gray-800 text-gray-400 border border-gray-700' : 'bg-gray-100 text-gray-500 border border-gray-200'
              }`}
            >
              v{version}
            </span>
          </div>
          {agent.description && (
            <p className={`text-xs mt-0.5 line-clamp-2 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>{agent.description}</p>
          )}
        </div>
      </div>
      {counts.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3">
          {counts.map((s) => (
            <span
              key={s.key}
              className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'
              }`}
            >
              {s.emoji} {s.n} {s.label}
            </span>
          ))}
        </div>
      )}
      <button
        type="button"
        onClick={() => onOpen(agent)}
        className="mt-auto w-full inline-flex items-center justify-center gap-1 px-2 py-1.5 rounded-lg text-xs font-medium bg-red-600 text-white hover:bg-red-700 transition-colors"
      >
        View agent <ChevronRight className="w-3.5 h-3.5" />
      </button>
    </div>
  );
};

/** Full agent detail: GIF, description, and the five lists. */
const AgentDetail = ({ agent, department, onClose }) => {
  const { darkMode } = useTheme();
  const subtle = darkMode ? 'text-gray-400' : 'text-gray-500';
  const sections = AGENT_SECTIONS.filter((s) => agent[s.key]?.length);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div
        className="relative w-full max-w-3xl my-8 bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className={`absolute top-4 right-4 p-1 rounded-full ${darkMode ? 'text-gray-400 hover:bg-gray-800' : 'text-gray-500 hover:bg-gray-100'}`}
          title="Close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="flex flex-col items-center text-center">
          <GifTile agent={agent} className="w-32 h-32 rounded-2xl mb-4" />
          <h2 className={`text-2xl font-bold tracking-tight ${darkMode ? 'text-white' : 'text-gray-900'}`}>{agent.name}</h2>
          <div className="flex items-center gap-2 mt-1">
            {department && (
              <span className={`text-xs font-medium ${subtle}`}>
                {department.emoji} {department.label}
              </span>
            )}
            <span className={`text-xs ${subtle}`}>· v{String(agent.version || '').trim() || '1.0'}</span>
          </div>
          {agent.description && (
            <p className={`mt-3 text-sm max-w-xl whitespace-pre-line ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              {agent.description}
            </p>
          )}
        </div>

        <div className="mt-6 space-y-5">
          {sections.length === 0 && <p className={`text-sm text-center ${subtle}`}>Details for this agent are coming soon.</p>}
          {sections.map((section) => (
            <div key={section.key}>
              <h3 className={`text-base font-semibold mb-2 text-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                <span className="mr-1.5">{section.emoji}</span>
                {section.label}
              </h3>
              <div className="space-y-2">
                {agent[section.key].map((item, idx) => (
                  <div key={item.id} className="flex items-start gap-3 px-3 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/60">
                    <span className={`shrink-0 text-xs font-semibold w-5 text-center pt-0.5 ${subtle}`}>{idx + 1}.</span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {item.skillId ? (
                          <Link
                            to={`/portal/technology/claude-skills?highlight=${encodeURIComponent(item.skillId)}`}
                            className="text-red-600 dark:text-red-400 hover:underline"
                          >
                            {item.title}
                          </Link>
                        ) : (
                          item.title
                        )}
                      </p>
                      {item.detail &&
                        (isUrl(item.detail) ? (
                          <a
                            href={item.detail.trim()}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-0.5 inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400 hover:underline break-all"
                          >
                            <ExternalLink className="w-3 h-3 shrink-0" /> {item.detail.trim()}
                          </a>
                        ) : (
                          <p className={`text-xs mt-0.5 whitespace-pre-line ${subtle}`}>{item.detail}</p>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/**
 * Portal Technology ▸ Agents. CRM-managed agent templates grouped by
 * department (same grouping as Automations), searchable, each opening a detail
 * view of its routines, abilities, library, skills, and examples.
 */
const Agents = ({ embedded: embeddedProp }) => {
  const { darkMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightParam = searchParams.get('highlight');

  const [rows, setRows] = useState([]);
  const [groupEmojis, setGroupEmojis] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [openId, setOpenId] = useState(null);

  const loadRows = useCallback(async () => {
    setLoading(true);
    try {
      const [list, emojis] = await Promise.all([listAgentTemplates(), getAgentGroupEmojis()]);
      setRows(list);
      setGroupEmojis(emojis);
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows();
  }, [loadRows]);

  // Deep link (?highlight=<id>) opens that agent's detail.
  useEffect(() => {
    if (highlightParam && rows.some((r) => r.id === highlightParam)) setOpenId(highlightParam);
  }, [highlightParam, rows]);

  const closeDetail = useCallback(() => {
    setOpenId(null);
    if (searchParams.get('highlight')) setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  const departments = useMemo(
    () => AGENT_DEPARTMENTS.map((d) => ({ ...d, emoji: groupEmojis[d.id] || d.emoji })),
    [groupEmojis]
  );

  const visibleSections = useMemo(() => {
    const searched = filterResourcesBySearch(rows, searchTerm);
    return departments
      .filter((d) => departmentFilter === 'all' || d.id === departmentFilter)
      .map((d) => ({ ...d, items: searched.filter((r) => r.category === d.id) }))
      .filter((d) => d.items.length > 0);
  }, [rows, searchTerm, departmentFilter, departments]);

  const openAgent = rows.find((r) => r.id === openId);

  const inputBase = `rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-red-500 ${
    darkMode ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400' : 'bg-white border-gray-200 text-gray-900 placeholder-gray-500'
  }`;

  return (
    <div className={embeddedProp === false ? 'relative p-6 min-h-screen text-gray-900 dark:text-white' : ''}>
      {embeddedProp === false && <PortalBackground />}
      <div className={embeddedProp === false ? 'relative' : ''}>
        {/* Search + department filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search
              className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
            />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search agents..."
              className={`w-full pl-9 pr-3 py-2 ${inputBase}`}
            />
          </div>
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className={`sm:w-56 px-3 py-2 ${inputBase}`}
          >
            <option value="all">All departments</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.emoji ? `${d.emoji} ` : ''}
                {d.label}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <BBALoader size={40} />
          </div>
        ) : visibleSections.length === 0 ? (
          <div
            className={`rounded-xl border p-8 text-center ${darkMode ? 'border-gray-800 bg-gray-900' : 'border-gray-200 bg-white'}`}
          >
            <Bot className={`w-8 h-8 mx-auto mb-3 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
            <h3 className={`text-base font-semibold mb-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              {rows.length === 0 ? 'No agents yet' : 'No agents match your search'}
            </h3>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {rows.length === 0 ? 'Agents will show up here as the BBA team publishes them.' : 'Try a different search or department.'}
            </p>
          </div>
        ) : (
          visibleSections.map((section) => (
            <div key={section.id} className="mt-10 first:mt-0">
              <div className="flex items-center justify-center gap-3 mb-5">
                <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
                <span className="shrink-0 w-10 h-10 rounded-xl bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
                  <span className="text-xl leading-none">{section.emoji}</span>
                </span>
                <h2
                  className={`text-xl md:text-2xl font-bold tracking-tight leading-tight text-center ${
                    darkMode ? 'text-white' : 'text-gray-900'
                  }`}
                >
                  {section.label}
                </h2>
                <span className={`h-px flex-1 max-w-[110px] ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {section.items.map((agent) => (
                  <div key={agent.id} id={`agent-${agent.id}`}>
                    <AgentCard agent={agent} onOpen={(a) => setOpenId(a.id)} />
                  </div>
                ))}
              </div>
            </div>
          ))
        )}

        {openAgent && (
          <AgentDetail
            agent={openAgent}
            department={departments.find((d) => d.id === openAgent.category)}
            onClose={closeDetail}
          />
        )}
      </div>
    </div>
  );
};

export default Agents;
